// utils/piiRemover.js - Precise PII removal for CV scanner

const PII_PATTERNS = {
    // Email addresses - precise pattern
    email: {
      pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      replacement: '[email removed]',
      description: 'Email addresses'
    },
    
    // Phone numbers - simple and direct patterns (fixed digit count)
    phone: {
      pattern: /\+27\d{10}|\+1\d{10}|0\d{9}/g,
      replacement: '[phone removed]',
      description: 'Phone numbers'
    },
    
    // South African ID numbers - exactly 13 digits with word boundaries
    saId: {
      pattern: /\b\d{13}\b/g,
      replacement: '[SA ID removed]',
      description: 'South African ID numbers'
    },
    
    // US Social Security Numbers - simple pattern
    ssn: {
      pattern: /\d{3}-\d{2}-\d{3,4}/g,
      replacement: '[SSN removed]',
      description: 'Social Security Numbers'
    },
    
    // Credit card numbers - 4 groups of 4 digits
    creditCard: {
      pattern: /\b(?:\d{4}[-\s]){3}\d{4}\b/g,
      replacement: '[credit card removed]',
      description: 'Credit card numbers'
    },
    
    // Passport numbers - letter(s) followed by digits, no spaces
    passport: {
      pattern: /\b[A-Z]{1,2}\d{6,9}\b/g,
      replacement: '[passport removed]',
      description: 'Passport numbers'
    },
    
    // Medical practice numbers - must have clear prefix and separator
    medicalId: {
      pattern: /\b(?:MP|HPCSA|Dr\.?\s+No\.?|Medical\s+Practice)\s*[:\-#]?\s*\d{6,10}\b/gi,
      replacement: '[medical ID removed]',
      description: 'Medical practice numbers'
    },
    
    // Medical practice numbers - alternative pattern for "MP 123456"
    medicalIdAlt: {
      pattern: /\bMP\s+\d{6,10}\b/gi,
      replacement: '[medical ID removed]',
      description: 'Medical practice numbers (alternative)'
    },
    
    // Driver's license - letters followed by digits
    driversLicense: {
      pattern: /\b[A-Z]{1,3}\d{6,12}\b/g,
      replacement: '[license removed]',
      description: 'Driver\'s license numbers'
    },
    
    // Street addresses - number + street name + street type
    address: {
      pattern: /\b\d+\s+[A-Za-z0-9\s,.-]{5,50}(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Circle|Cir|Way|Place|Pl|Parkway|Pkwy|Terrace|Ter|Close|Cl)\b/gi,
      replacement: '[address removed]',
      description: 'Street addresses'
    },
    
    // IP addresses
    ipAddress: {
      pattern: /\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/g,
      replacement: '[IP removed]',
      description: 'IP addresses'
    },
    
    // URLs
    url: {
      pattern: /https?:\/\/(?:[-\w.])+(?:\:[0-9]+)?(?:\/(?:[\w\/_.])*(?:\?(?:[\w&=%.])*)?(?:\#(?:[\w.])*)?)?/g,
      replacement: '[URL removed]',
      description: 'URLs'
    },
    
    // Dates that could be DOB - only specific date formats
    dateOfBirth: {
      pattern: /\b(?:0[1-9]|[12][0-9]|3[01])[-\/](?:0[1-9]|1[0-2])[-\/](?:19[0-9]{2}|20[0-2][0-9])\b/g,
      replacement: '[date removed]',
      description: 'Dates (DD/MM/YYYY or DD-MM-YYYY)'
    }
  };
  
  // Streamlined configuration - removed problematic patterns
  const DEFAULT_CONFIG = {
    email: { remove: true, replacement: '[email removed]' },
    phone: { remove: true, replacement: '[phone removed]' },
    saId: { remove: true, replacement: '[SA ID removed]' },
    ssn: { remove: true, replacement: '[SSN removed]' },
    creditCard: { remove: true, replacement: '[credit card removed]' },
    passport: { remove: true, replacement: '[passport removed]' },
    medicalId: { remove: true, replacement: '[medical ID removed]' },
    medicalIdAlt: { remove: true, replacement: '[medical ID removed]' },
    driversLicense: { remove: false, replacement: '[license removed]' }, // Often false positives
    address: { remove: true, replacement: '[address removed]' },
    ipAddress: { remove: true, replacement: '[IP removed]' },
    url: { remove: false, replacement: '[URL removed]' }, // Keep URLs for portfolios
    dateOfBirth: { remove: true, replacement: '[date removed]' }
    // Removed bankAccount and zipCode as they cause too many false positives
  };
  
  /**
   * Validate input text
   */
  function validateInput(text) {
    if (text === null || text === undefined) {
      throw new TypeError('Input cannot be null or undefined');
    }
    if (typeof text !== 'string') {
      throw new TypeError('Input must be a string');
    }
    return text;
  }
  
  /**
   * Merge user options with default configuration
   */
  function mergeConfig(userOptions = {}) {
    const config = {};
    
    // Start with defaults
    for (const [key, value] of Object.entries(DEFAULT_CONFIG)) {
      config[key] = { ...value };
    }
    
    // Override with user options
    for (const [key, value] of Object.entries(userOptions)) {
      if (config[key]) {
        config[key] = { ...config[key], ...value };
      } else {
        config[key] = value;
      }
    }
    
    return config;
  }
  
  /**
   * Remove PII from text (simple version)
   */
  function removePII(text, options = {}) {
    validateInput(text);
    
    const config = mergeConfig(options);
    let cleanedText = text;
    
    for (const [type, settings] of Object.entries(config)) {
      if (!settings.remove) continue;
      
      const patternInfo = PII_PATTERNS[type];
      if (!patternInfo) continue;
      
      const pattern = patternInfo.pattern;
      const replacement = settings.replacement || patternInfo.replacement || '[PII removed]';
      
      cleanedText = cleanedText.replace(pattern, replacement);
    }
    
    return cleanedText;
  }
  
  /**
   * Remove PII from text with detailed results - SEQUENTIAL PROCESSING
   */
  function removePIIDetailed(text, options = {}) {
    validateInput(text);
    
    const config = mergeConfig(options);
    let cleanedText = text;
    const removedItems = [];
    
    // Process patterns in order of specificity (most specific first)
    const processingOrder = [
      'email',
      'phone', 
      'saId',
      'medicalId',
      'medicalIdAlt',
      'passport',
      'ssn',
      'creditCard',
      'address',
      'ipAddress',
      'url',
      'dateOfBirth',
      'driversLicense'
    ];
    
    for (const type of processingOrder) {
      const settings = config[type];
      if (!settings || !settings.remove) continue;
      
      const patternInfo = PII_PATTERNS[type];
      if (!patternInfo) continue;
      
      const pattern = patternInfo.pattern;
      const replacement = settings.replacement || patternInfo.replacement || '[PII removed]';
      
      // Find matches in current state of text
      const matches = cleanedText.match(pattern);
      if (matches && matches.length > 0) {
        // Store unique matches only
        const uniqueMatches = [...new Set(matches)];
        
        removedItems.push({
          type,
          count: matches.length,
          items: uniqueMatches,
          description: patternInfo.description
        });
        
        // Replace all matches
        cleanedText = cleanedText.replace(pattern, replacement);
      }
    }
    
    // Calculate statistics
    const originalLength = text.length;
    const cleanedLength = cleanedText.length;
    const reductionPercentage = originalLength > 0 
      ? Math.round(((originalLength - cleanedLength) / originalLength) * 100) 
      : 0;
    
    return {
      cleanedText,
      removedItems,
      originalLength,
      cleanedLength,
      reductionPercentage
    };
  }
  
  /**
   * Detect PII in text without removing it
   */
  function detectPII(text, options = {}) {
    validateInput(text);
    
    const config = mergeConfig(options);
    const detectedItems = [];
    
    for (const [type, settings] of Object.entries(config)) {
      const patternInfo = PII_PATTERNS[type];
      if (!patternInfo) continue;
      
      const pattern = patternInfo.pattern;
      const matches = [];
      const positions = [];
      let match;
      
      // Create a fresh regex for iteration
      const regex = new RegExp(pattern.source, pattern.flags);
      
      while ((match = regex.exec(text)) !== null) {
        matches.push(match[0]);
        positions.push({
          start: match.index,
          end: match.index + match[0].length,
          value: match[0]
        });
        
        // Prevent infinite loop on non-global patterns
        if (!pattern.global) break;
      }
      
      if (matches.length > 0) {
        detectedItems.push({
          type,
          count: matches.length,
          items: [...new Set(matches)], // Remove duplicates
          positions,
          description: patternInfo.description
        });
      }
    }
    
    const totalMatches = detectedItems.reduce((sum, item) => sum + item.count, 0);
    
    return {
      text,
      detectedItems,
      hasPII: detectedItems.length > 0,
      totalMatches,
      types: detectedItems.map(item => item.type)
    };
  }
  
  /**
   * Process multiple texts in batch
   */
  function processBatch(texts, options = {}) {
    if (!Array.isArray(texts)) {
      throw new TypeError('Input must be an array of strings');
    }
    
    return texts.map((text, index) => {
      try {
        const result = removePIIDetailed(text, options);
        return {
          index,
          success: true,
          ...result
        };
      } catch (error) {
        return {
          index,
          success: false,
          error: error.message,
          originalText: text
        };
      }
    });
  }
  
  /**
   * Get available PII types and their descriptions
   */
  function getAvailableTypes() {
    return Object.keys(PII_PATTERNS).map(type => ({
      type,
      description: PII_PATTERNS[type].description,
      defaultReplacement: PII_PATTERNS[type].replacement
    }));
  }
  
  /**
   * Create a custom PII pattern
   */
  function createCustomPattern(type, pattern, replacement, description) {
    return {
      [type]: {
        pattern,
        replacement,
        description
      }
    };
  }
  
  // Export functions
  export {
    removePII,
    removePIIDetailed,
    detectPII,
    processBatch,
    getAvailableTypes,
    createCustomPattern,
    PII_PATTERNS,
    DEFAULT_CONFIG
  };
  
  export default removePII;